import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, CheckCircle2, XCircle, Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ICON = { success: [CheckCircle2, "text-emerald-600"], error: [XCircle, "text-red-600"], info: [Info, "text-blue-600"] };

export default function NotificationBell() {
  const { refreshBalance } = useAuth();
  const [data, setData] = useState({ items: [], unread: 0 });
  const seen = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/notifications");
      if (seen.current) {
        const fresh = d.items.filter((n) => !seen.current.has(n.id) && !n.read);
        fresh.forEach((n) => (n.kind === "error" ? toast.error : toast.success)(n.title, { description: n.message, duration: 8000 }));
        if (fresh.length) refreshBalance();
      }
      seen.current = new Set(d.items.map((n) => n.id));
      setData(d);
    } catch {}
  }, [refreshBalance]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const markRead = async (open) => {
    if (open || !data.unread) return;
    await api.post("/notifications/read-all");
    setData((d) => ({ unread: 0, items: d.items.map((n) => ({ ...n, read: true })) }));
  };

  return (
    <Popover onOpenChange={markRead}>
      <PopoverTrigger asChild>
        <button className="relative rounded-md p-2 text-slate-500 hover:bg-slate-100" data-testid="notifications-button">
          <Bell className="h-4 w-4" />
          {data.unread > 0 && <span className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-red-600 px-1 text-[9px] font-bold text-white" data-testid="notifications-unread-count">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0" data-testid="notifications-panel">
        <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold">Notifications</div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <div className="px-4 py-8 text-center text-sm text-slate-400">No notifications yet.</div>}
          {data.items.map((n) => {
            const [I, c] = ICON[n.kind] || ICON.info;
            return (
              <div key={n.id} className={`flex gap-3 border-b border-slate-50 px-4 py-3 text-sm ${n.read ? "" : "bg-blue-50/50"}`} data-testid={`notification-${n.ref}`}>
                <I className={`mt-0.5 h-4 w-4 shrink-0 ${c}`} />
                <div className="min-w-0">
                  <div className="font-semibold">{n.title}</div>
                  <div className="text-xs text-slate-600">{n.message}</div>
                  {n.utr && <div className="mt-0.5 font-mono text-[11px] text-slate-500" data-testid="notification-utr">UTR: {n.utr}</div>}
                  <div className="mt-0.5 text-[10px] text-slate-400">{fmtDate(n.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
